import { gql, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import Avatar from '../../components/Avatar';
import type {
  PublicUserMentionQuery,
  PublicUserMentionQueryVariables,
  UserMentionQuery,
  UserMentionQueryVariables,
} from '../../graphql/generated/graphqlTypes';
import postPolicyCheck, { PostPolicyAction } from '../../post/postPolicyCheck';
import UserDetailPreviewPopOver from '../../user/UserDetailPreviewPopOver';
import useSlug from '../../utils/router/useSlug';
import userPath from '../../utils/url/userPath';
import useIsPublicView from '../../utils/useIsPublicView';
import MentionBase from './MentionBase';
import styles from './UserMention.module.scss';

const POST_QUERY = gql`
  query UserMentionPost($id: ID!) {
    post(id: $id) {
      id
      ...postPolicyCheck_post
    }
  }
  ${postPolicyCheck.fragments.post}
`;

const USER_QUERY = gql`
  query UserMention($id: ID!) {
    user(id: $id) {
      id
      name
      deactivatedAt
      ...Avatar_user
      ...postPolicyCheck_user
    }
  }
  ${Avatar.fragments.user}
  ${postPolicyCheck.fragments.user}
`;

const PUBLIC_USER_QUERY = gql`
  query PublicUserMention($id: ID!) {
    user: publicUser(id: $id) {
      id
      name
      ...Avatar_user
    }
  }
  ${Avatar.fragments.user}
`;

const propTypes = {
  content: PropTypes.string,
  mentionText: PropTypes.string,
  id: PropTypes.string.isRequired,
  updateCache: PropTypes.func,
  link: PropTypes.bool.isRequired,
  showHoverCard: PropTypes.bool,
};

const defaultProps = {
  content: '',
  mentionText: '',
  updateCache: () => {},
  showHoverCard: false,
};

interface UserMentionProps {
  id: string;
  content: string | null;
  mentionText?: string | null;
  updateCache?: (name?: string) => void;
  link: boolean;
  showHoverCard?: boolean;
  singleLine?: boolean;
  newTab?: boolean;
}

const UserMention = ({
  id,
  content,
  mentionText,
  updateCache,
  link,
  showHoverCard,
  singleLine,
  newTab,
}: UserMentionProps) => {
  const isPublicView = useIsPublicView();
  const [postId] = useSlug('postSlug');

  const { data: { user } = {} } = useQuery<
    UserMentionQuery | PublicUserMentionQuery,
    UserMentionQueryVariables | PublicUserMentionQueryVariables
  >(isPublicView ? PUBLIC_USER_QUERY : USER_QUERY, { variables: { id } });
  const { data: { post } = {} } = useQuery(POST_QUERY, {
    skip: isPublicView || !postId,
    variables: { id: postId },
  });

  useEffect(() => {
    if (!isPublicView && user && updateCache) {
      updateCache(user.name);
    }
  }, [updateCache, isPublicView, user]);

  const internalUser = user && 'deactivatedAt' in user ? user : null;

  const noAccess =
    !!internalUser?.deactivatedAt ||
    (internalUser &&
      post &&
      !postPolicyCheck(PostPolicyAction.ViewPost, post, internalUser));

  const mention = (
    <MentionBase
      icon={user && <Avatar user={user} size="small" />}
      type="user"
      content={mentionText || (user?.name ?? content ?? undefined)}
      iconClassName={noAccess ? styles.noAccessAvatar : undefined}
      contentClassName={noAccess ? styles.noAccessContent : undefined}
      to={link && user ? userPath(user) : undefined}
      singleLine={singleLine}
      newTab={newTab}
    />
  );

  if (!user || !showHoverCard) {
    return mention;
  }

  return (
    <UserDetailPreviewPopOver
      allowFlip
      display="inline"
      userId={user.id}
      placement="right"
    >
      {mention}
    </UserDetailPreviewPopOver>
  );
};

UserMention.propTypes = propTypes;
UserMention.defaultProps = defaultProps;

export default UserMention;
